import axios from 'axios';
import { authService } from './authService';
import config from '../config/environment';

// Master Data Interfaces
export interface State {
  stateId: number;
  stateName: string;
  stateCode: string;
  isUnionTerritory: boolean;
}

export interface City {
  cityId: number;
  cityName: string;
  stateId: number;
  stateName: string;
  stateCode: string;
}

export interface MasterDataResponse {
  states: State[];
  cities: City[];
  // Future master data can be added here
  // services?: Service[];
  // categories?: Category[];
  // etc.
}

// Create axios instance for master data API calls
const masterDataApi = axios.create({
  baseURL: config.authServerUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include access token
masterDataApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
masterDataApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await authService.refreshAccessToken();
        const token = localStorage.getItem('access_token');
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return masterDataApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        authService.logoutLocal();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Cache keys
const CACHE_KEYS = {
  ALL_MASTER_DATA: 'masterData_all',
  STATES: 'masterData_states',
  CITIES: 'masterData_cities',
  CITIES_BY_STATE: 'masterData_cities_state_',
} as const;

// Cache duration (24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Helper function to check if cached data is valid
const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_DURATION;
};

// Helper function to get cached data
const getCachedData = <T>(key: string): T | null => {
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (isCacheValid(timestamp)) {
        return data;
      } else {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error('Error reading cache:', error);
    localStorage.removeItem(key);
  }
  return null;
};

// Helper function to set cached data
const setCachedData = <T>(key: string, data: T): void => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

export const masterDataService = {
  /**
   * Get all master data (states, cities, etc.)
   * Uses caching for performance
   */
  getAllMasterData: async (): Promise<MasterDataResponse> => {
    try {
      // Check cache first
      const cached = getCachedData<MasterDataResponse>(CACHE_KEYS.ALL_MASTER_DATA);
      if (cached) {
        console.log('📦 Using cached master data');
        return cached;
      }

      console.log('🌐 Fetching all master data from API...');
      
      const response = await masterDataApi.get<MasterDataResponse>('/api/master-data/all');
      
      console.log('✅ Master data fetched successfully:', {
        states: response.data.states.length,
        cities: response.data.cities.length,
      });
      
      // Cache the response
      setCachedData(CACHE_KEYS.ALL_MASTER_DATA, response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching master data:', error);
      
      // Try to return cached data even if expired in case of network issues
      const cached = getCachedData<MasterDataResponse>(CACHE_KEYS.ALL_MASTER_DATA);
      if (cached) {
        console.log('⚠️ Using expired cached master data due to API error');
        return cached;
      }
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Master data endpoints not found');
        }
        
        const errorMessage = error.response?.data?.message || 'Failed to fetch master data';
        throw new Error(errorMessage);
      }
      
      throw new Error('Failed to fetch master data');
    }
  },

  /**
   * Get all states
   */
  getStates: async (): Promise<State[]> => {
    try {
      // Check cache first
      const cached = getCachedData<State[]>(CACHE_KEYS.STATES);
      if (cached) {
        console.log('📦 Using cached states data');
        return cached;
      }

      console.log('🌐 Fetching states from API...');
      
      const response = await masterDataApi.get<State[]>('/api/master-data/states');
      
      console.log('✅ States fetched successfully:', response.data.length, 'states');
      
      // Cache the response
      setCachedData(CACHE_KEYS.STATES, response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching states:', error);
      
      // Fallback to all master data
      try {
        const masterData = await masterDataService.getAllMasterData();
        return masterData.states;
      } catch (fallbackError) {
        console.error('❌ Fallback to master data failed:', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Get all cities
   */
  getCities: async (): Promise<City[]> => {
    try {
      // Check cache first
      const cached = getCachedData<City[]>(CACHE_KEYS.CITIES);
      if (cached) {
        console.log('📦 Using cached cities data');
        return cached;
      }

      console.log('🌐 Fetching cities from API...');
      
      const response = await masterDataApi.get<City[]>('/api/master-data/cities');
      
      console.log('✅ Cities fetched successfully:', response.data.length, 'cities');
      
      // Cache the response
      setCachedData(CACHE_KEYS.CITIES, response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching cities:', error);
      
      // Fallback to all master data
      try {
        const masterData = await masterDataService.getAllMasterData();
        return masterData.cities;
      } catch (fallbackError) {
        console.error('❌ Fallback to master data failed:', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Get cities for a specific state
   */
  getCitiesByState: async (stateId: number): Promise<City[]> => {
    try {
      const cacheKey = `${CACHE_KEYS.CITIES_BY_STATE}${stateId}`;
      
      // Check cache first
      const cached = getCachedData<City[]>(cacheKey);
      if (cached) {
        console.log(`📦 Using cached cities data for state ${stateId}`);
        return cached;
      }

      console.log(`🌐 Fetching cities for state ${stateId} from API...`);
      
      const response = await masterDataApi.get<City[]>(`/api/master-data/cities/state/${stateId}`);
      
      console.log(`✅ Cities for state ${stateId} fetched successfully:`, response.data.length, 'cities');
      
      // Cache the response
      setCachedData(cacheKey, response.data);
      
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching cities for state ${stateId}:`, error);
      
      // Fallback to filtering from all cities
      try {
        const allCities = await masterDataService.getCities();
        const filteredCities = allCities.filter((city: City) => city.stateId === stateId);
        
        console.log(`⚠️ Using filtered cities from cache for state ${stateId}:`, filteredCities.length, 'cities');
        
        return filteredCities;
      } catch (fallbackError) {
        console.error('❌ Fallback to all cities failed:', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Get state by ID
   */
  getStateById: async (stateId: number): Promise<State | null> => {
    try {
      const states = await masterDataService.getStates();
      return states.find((state: State) => state.stateId === stateId) || null;
    } catch (error) {
      console.error(`❌ Error finding state ${stateId}:`, error);
      return null;
    }
  },

  /**
   * Get city by ID
   */
  getCityById: async (cityId: number): Promise<City | null> => {
    try {
      const cities = await masterDataService.getCities();
      return cities.find((city: City) => city.cityId === cityId) || null;
    } catch (error) {
      console.error(`❌ Error finding city ${cityId}:`, error);
      return null;
    }
  },

  /**
   * Search states by name
   */
  searchStates: async (searchTerm: string): Promise<State[]> => {
    try {
      const states = await masterDataService.getStates();
      const term = searchTerm.toLowerCase();
      return states.filter((state: State) => 
        state.stateName.toLowerCase().includes(term) ||
        state.stateCode.toLowerCase().includes(term)
      );
    } catch (error) {
      console.error('❌ Error searching states:', error);
      return [];
    }
  },

  /**
   * Search cities by name
   */
  searchCities: async (searchTerm: string, stateId?: number): Promise<City[]> => {
    try {
      const cities = stateId 
        ? await masterDataService.getCitiesByState(stateId)
        : await masterDataService.getCities();
        
      const term = searchTerm.toLowerCase();
      return cities.filter((city: City) => 
        city.cityName.toLowerCase().includes(term)
      );
    } catch (error) {
      console.error('❌ Error searching cities:', error);
      return [];
    }
  },

  /**
   * Clear all cached master data
   */
  clearCache: (): void => {
    console.log('🗑️ Clearing master data cache...');
    Object.values(CACHE_KEYS).forEach(key => {
      if (key === CACHE_KEYS.CITIES_BY_STATE) {
        // Clear all state-specific city caches
        Object.keys(localStorage).forEach(storageKey => {
          if (storageKey.startsWith(key)) {
            localStorage.removeItem(storageKey);
          }
        });
      } else {
        localStorage.removeItem(key);
      }
    });
    console.log('✅ Master data cache cleared');
  },

  /**
   * Force refresh master data
   */
  refreshMasterData: async (): Promise<MasterDataResponse> => {
    console.log('🔄 Force refreshing master data...');
    masterDataService.clearCache();
    return await masterDataService.getAllMasterData();
  }
};

export default masterDataService;